/**
 * 광역권역별 시각 자산 메타.
 *
 * 백엔드는 `RegionDetail.isFullCoverage` / `subRegionList`로 분기·필터링 데이터를 주지만,
 * 시각 자산(SVG 지도, full coverage 메시지)은 클라이언트가 보유.
 * 본 파일은 region 한글 이름 → 시각 자산 매핑만 담당.
 *
 * coverage/subRegionList는 API에서 받으므로 본 파일에 두지 않는다 — 이중 소스 방지.
 *
 * 새 광역권역 추가 시:
 *  1. 본 파일에 항목 추가 (Map 컴포넌트, full coverage 메시지)
 *  2. `components/RegionMap/maps/{광역권역}Map.tsx` 작성
 */

import type { ComponentType } from 'react'

import GyeonggiMap from '../components/RegionMap/maps/GyeonggiMap'
import IncheonMap from '../components/RegionMap/maps/IncheonMap'
import SeoulMap from '../components/RegionMap/maps/SeoulMap'

/** 모든 region 시각 자산 컴포넌트가 받는 공통 props */
export interface RegionMapComponentProps {
  /** 하위 광역권역 한글값 ('경기 북부' 등). null이면 전체. subRegion 분기 없는 region(서울)은 무시 */
  subRegion?: string | null
  /**
   * 시 라벨 클릭 시 호출. 인자는 백엔드 SiGunGu.name(한글, 예: "수원시").
   * 라벨 인터랙션 없는 region(서울)은 호출 안 함.
   */
  onLabelClick?: (sigunguName: string) => void
}

export interface RegionMapMeta {
  /** 광역권역의 시각 자산(시도 SVG 지도) — 본 광역권역 카드에 렌더 */
  Map: ComponentType<RegionMapComponentProps>
  /**
   * isFullCoverage=true일 때 표시되는 안내 메시지.
   * `highlight` substring을 brand orange(#ff5626)로 강조, 나머지는 default text color.
   * `\n` 으로 줄바꿈 처리 (CSS white-space: pre-line).
   *
   * isFullCoverage=false면 메시지 대신 범례(Legend) 노출 → 본 필드 불필요.
   */
  message?: {
    text: string
    highlight: string
  }
}

/**
 * 한글 region 이름 → 시각 자산 메타.
 *
 * lookup 결과 undefined면 `RegionMap` 라우터가 graceful 처리 (시각 자산 없는 region은 메시지·범례만 노출 또는 null).
 */
export const REGION_MAPS: Record<string, RegionMapMeta> = {
  서울: {
    Map: SeoulMap,
    message: {
      text: '서울 전 지역 어디서든\n수거 가능해요!',
      highlight: '서울 전 지역',
    },
  },
  경기: {
    Map: GyeonggiMap,
    // message X — isFullCoverage=false면 범례(Legend) 표시
  },
  인천: {
    Map: IncheonMap,
    // message X — isFullCoverage=false면 범례(Legend) 표시
  },
  // 경상 — 디자이너 자료 후 추가
}

export interface RegionMessage {
  text: string
  highlight: string
}

/** 메시지의 highlight 부분을 분리 — JSX 렌더용 헬퍼 */
export function splitMessageByHighlight(message: RegionMessage) {
  const idx = message.text.indexOf(message.highlight)
  if (idx === -1) return { before: message.text, highlight: '', after: '' }
  return {
    before: message.text.slice(0, idx),
    highlight: message.highlight,
    after: message.text.slice(idx + message.highlight.length),
  }
}
